import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Sparkles, Trophy, TrendingUp, TrendingDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * AI-generated comparative narrative + per-metric winners.
 * Works for both `company` and `policy` compare flows.
 *
 * Props:
 *  - kind: "company" | "policy"
 *  - entities: array of entity dicts (result of /companies/compare or /policy/compare)
 */
export const CompareInsights = ({ kind, entities }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!entities || entities.length < 2) {
      setData(null);
      return;
    }
    const myReq = ++reqIdRef.current;
    setLoading(true);
    axios
      .post(
        `${API}/compare/insights`,
        { kind, entities },
        { timeout: 45000 }
      )
      .then((r) => {
        if (reqIdRef.current === myReq) setData(r.data || null);
      })
      .catch(() => {
        if (reqIdRef.current === myReq) setData(null);
      })
      .finally(() => {
        if (reqIdRef.current === myReq) setLoading(false);
      });
  }, [kind, JSON.stringify((entities || []).map((e) => e.domain || e.country))]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!entities || entities.length < 2) return null;

  return (
    <div
      className="relative bg-gradient-to-br from-violet-500/[0.04] via-white/[0.02] to-amber-500/[0.03] border border-violet-500/20 rounded-2xl p-5 overflow-hidden"
      data-testid={`compare-insights-${kind}`}
    >
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-violet-400/40 to-transparent" />

      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-3.5 h-3.5 text-violet-300" />
        <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-violet-300">
          AI Insight Summary
        </h3>
        {loading && (
          <span className="text-[9px] text-slate-500 animate-pulse">analyzing…</span>
        )}
      </div>

      {/* Narrative */}
      <div className="min-h-[60px]">
        {loading && !data && (
          <div className="space-y-2" data-testid="compare-insights-skeleton">
            <div className="h-3 w-full bg-white/[0.05] rounded animate-pulse" />
            <div className="h-3 w-11/12 bg-white/[0.05] rounded animate-pulse" />
            <div className="h-3 w-4/5 bg-white/[0.05] rounded animate-pulse" />
          </div>
        )}
        {data?.narrative && (
          <p
            className="text-[13px] text-slate-200 leading-relaxed font-medium"
            data-testid="compare-insights-narrative"
          >
            {data.narrative}
          </p>
        )}
        {!loading && !data?.narrative && (
          <p className="text-xs text-slate-500 italic">
            Narrative unavailable. Comparison data is shown below.
          </p>
        )}
      </div>

      {/* Winners strip */}
      {data?.winners?.length > 0 && (
        <div className="mt-5 pt-5 border-t border-white/[0.06]">
          <div className="flex items-center gap-1.5 mb-3">
            <Trophy className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
              Best in Class
            </span>
          </div>
          <div
            className={`grid gap-2 ${
              data.winners.length <= 4
                ? "grid-cols-2 md:grid-cols-4"
                : "grid-cols-2 md:grid-cols-5"
            }`}
            data-testid="compare-insights-winners"
          >
            {data.winners.map((w) => (
              <div
                key={w.label}
                className="rounded-lg p-3 bg-white/[0.02] border border-white/[0.05]"
                data-testid={`winner-${w.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                  {w.label}
                </p>
                <p
                  className="text-xs font-black leading-tight truncate"
                  style={{ color: w.color }}
                  title={w.winner}
                >
                  {w.winner}
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5 tabular-nums">
                  {typeof w.value === "number" ? w.value.toLocaleString() : w.value}
                  {w.unit ? ` ${w.unit}` : ""}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dimension leaders (policy only) — shows biggest gaps */}
      {data?.dim_leaders?.length > 0 && (
        <div className="mt-5 pt-5 border-t border-white/[0.06]">
          <div className="flex items-center gap-1.5 mb-3">
            <TrendingUp className="w-3 h-3 text-emerald-400" />
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
              Biggest Dimension Gaps
            </span>
            <span className="text-[9px] text-slate-600">
              (sorted by spread)
            </span>
          </div>
          <div className="space-y-1.5" data-testid="compare-insights-dim-leaders">
            {data.dim_leaders.slice(0, 5).map((d) => {
              const pct = Math.max(0, Math.min(100, d.spread));
              return (
                <div
                  key={d.dimension}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.04]"
                  data-testid={`dim-gap-${d.dimension.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <p className="text-[11px] font-bold text-slate-300 w-36 flex-shrink-0 truncate">
                    {d.dimension}
                  </p>
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <span className="text-[10px] text-emerald-400 font-bold truncate max-w-[30%]">
                      {d.leader}
                    </span>
                    <span className="text-[10px] text-emerald-400 tabular-nums flex-shrink-0">
                      {d.leader_value}
                    </span>
                    <div className="flex-1 h-1 bg-white/[0.04] rounded-full overflow-hidden mx-1">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-rose-500 to-emerald-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-rose-400 tabular-nums flex-shrink-0">
                      {d.laggard_value}
                    </span>
                    <span className="text-[10px] text-rose-400 font-bold truncate max-w-[30%]">
                      {d.laggard}
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-500 tabular-nums w-14 text-right flex-shrink-0">
                    Δ{d.spread}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Consensus note if all dimensions are close */}
      {data?.dim_leaders?.length > 0 &&
        data.dim_leaders[0]?.spread < 10 && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-emerald-500/[0.06] border border-emerald-500/20 flex items-center gap-2">
            <TrendingDown className="w-3 h-3 text-emerald-400" />
            <p className="text-[11px] text-emerald-200/80">
              Strong consensus across dimensions — all selected entities score
              within {data.dim_leaders[0].spread} points on every measure.
            </p>
          </div>
        )}
    </div>
  );
};

export default CompareInsights;
